/**
 * Month view — flat CSS Grid. Extracted verbatim (behavior-preserving) from
 * EventListModule's `renderMonthView`. Every value the original render function
 * read from component scope is now an explicit, fully-typed prop. The hover
 * highlight state (`hoveredMdEventId`) had no external readers, so it lives here
 * as module-local state — identical behavior, smaller orchestrator surface.
 *
 * The dependent helpers `getDateOnly`, `getLastOccupiedDay`, `isMultiDayEvent`,
 * `getCalendarDays`, and `formatTimeCompact` were used ONLY by the month view,
 * so they move with it as pure module-local helpers.
 *
 * `DraggableEvent` / `DroppableDay` are the dnd-kit wrappers (used only by the
 * month grid) and move here too. `getEventBg` / `getEventDotColor` are pure lib
 * helpers imported directly from ../lib/eventColors rather than threaded through
 * props.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { AgentShimmer } from '@/common/components/AgentShimmer';
import { useEscapeKey } from '@/common/hooks/useEscapeKey';
import { calendarFlash } from '@/lib/calendarFlash';
import {
  isDateOnlyString,
  parseEventDate,
  startOfLocalDay,
  isAllDayish,
} from '../dateTimeUtils';
import { getEventBg, getEventDotColor } from '../lib/eventColors';
import { useCalendar24Hour, formatEventTime } from '../lib/timeFormat';
import { layoutMultiDaySpans, isMultiDayEvent, getLastOccupiedDay } from './monthLayout';
import type { EventObject } from '../eventTypes';

// Draggable event component for calendar
function DraggableEvent({
  event,
  children,
  onClick,
}: {
  event: EventObject;
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
}) {
  // Task-derived events are not drag-reschedulable (dragging dueAt is out of
  // scope) — disable dnd so they can't be picked up; they stay click-to-open.
  const isTaskEvent = !!event._sourceTaskId;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: event.id,
    data: { event },
    disabled: isTaskEvent,
  });

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isTaskEvent ? 'pointer' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

// Droppable day cell component for calendar
function DroppableDay({
  date,
  children,
  className,
  onClick,
  style: styleProp,
}: {
  date: Date;
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  style?: React.CSSProperties;
}) {
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dateStr}`,
    data: { date },
  });

  return (
    <div
      ref={setNodeRef}
      className={className}
      onClick={onClick}
      style={{
        ...styleProp,
        backgroundColor: isOver ? 'rgba(var(--color-primary-rgb), 0.1)' : undefined,
      }}
    >
      {children}
    </div>
  );
}

const formatTimeCompact = (dateStr: string, allDay?: boolean, use24h = false) => {
  if (allDay || isDateOnlyString(dateStr)) return '';
  const date = parseEventDate(dateStr);
  if (!date) return '';
  const hours = date.getHours();
  const minutes = date.getMinutes();
  if (use24h) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'a' : 'p';
  // Omit minutes if :00
  if (minutes === 0) {
    return `${hour12}${ampm}`;
  }
  return `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`;
};

// Calendar helper functions
const getCalendarDays = (year: number, month: number) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPadding = firstDay.getDay(); // 0 = Sunday
  const daysInMonth = lastDay.getDate();

  // Get previous month's last days for padding
  const prevMonthLastDay = new Date(year, month, 0).getDate();

  const days: Array<{ date: Date; isCurrentMonth: boolean }> = [];

  // Previous month padding
  for (let i = startPadding - 1; i >= 0; i--) {
    days.push({
      date: new Date(year, month - 1, prevMonthLastDay - i),
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Next month padding (only complete the current week, don't fill to 6 rows)
  const remainingDaysInWeek = (7 - (days.length % 7)) % 7;
  for (let i = 1; i <= remainingDaysInWeek; i++) {
    days.push({
      date: new Date(year, month + 1, i),
      isCurrentMonth: false,
    });
  }

  return days;
};

// Date-only comparison uses the shared `startOfLocalDay` (dateTimeUtils).
// `getLastOccupiedDay` and `isMultiDayEvent` now live in ./monthLayout (shared
// with the lane-assignment algorithm and unit-tested there).

export interface MonthViewProps {
  calendarDate: Date;
  expandedEvents: EventObject[];
  /** #3952: ids flagged as same-slot suspected duplicates → mark the chip. */
  suspectedDuplicateIds: Set<string>;
  monthGridRef: React.RefObject<HTMLDivElement | null>;
  isToday: (date: Date) => boolean;
  handleCalendarSlotClick: (date: Date, hour: number) => void;
  handleEventClick: (event: EventObject) => void;
  setCalendarDate: React.Dispatch<React.SetStateAction<Date>>;
  setTimeFilterState: React.Dispatch<React.SetStateAction<'today' | 'week' | 'month'>>;
  onCompleteTaskEvent: (event: EventObject) => void;
  onOpenTaskEvent: (event: EventObject) => void;
}

/**
 * Google-style day detail popover, opened from a month cell's "+N more". Shows
 * the weekday + date and a scrollable list of every event/task on that day.
 * `events` is computed by the parent on each render, so completing a task-event
 * (which invalidates the query → MonthView re-renders) updates the list live.
 */
function DayEventsPopover({
  date,
  rect,
  events,
  suspectedDuplicateIds,
  onClose,
  onCompleteTaskEvent,
  onOpenTaskEvent,
  handleEventClick,
}: {
  date: Date;
  rect: DOMRect;
  events: EventObject[];
  suspectedDuplicateIds: Set<string>;
  onClose: () => void;
  onCompleteTaskEvent: (event: EventObject) => void;
  onOpenTaskEvent: (event: EventObject) => void;
  handleEventClick: (event: EventObject) => void;
}) {
  useEscapeKey(onClose);
  const [use24h] = useCalendar24Hour();

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
  const width = Math.max(rect.width + 56, 264);
  const maxHeight = Math.min(440, window.innerHeight - 24);
  let left = rect.left - 16;
  let top = rect.top - 16;
  if (left + width > window.innerWidth - 8) left = window.innerWidth - width - 8;
  if (left < 8) left = 8;
  if (top + maxHeight > window.innerHeight - 8) top = Math.max(8, window.innerHeight - maxHeight - 8);
  if (top < 8) top = 8;

  return createPortal(
    <>
      <div className="xv-day-popover-backdrop" onClick={onClose} />
      <div
        className="xv-day-popover"
        style={{ left, top, width, maxHeight }}
        role="dialog"
        aria-label={`Events on ${weekday} ${date.getDate()}`}
      >
        <div className="xv-day-popover-header">
          <span className="xv-day-popover-weekday">{weekday}</span>
          <button type="button" className="xv-day-popover-close" aria-label="Close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
          <div className="xv-day-popover-datecircle">{date.getDate()}</div>
        </div>
        <div className="xv-day-popover-list xv-scroll">
          {events.length === 0 && <div className="xv-day-popover-empty">No events</div>}
          {events.map((event) => {
            const accent = getEventDotColor(event.eventType, event.providerCalendarId, event.metadata?.customColor);
            const occKey = event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id;
            const start = parseEventDate(event.startAt);
            const timeLabel = (!event.allDay && !isDateOnlyString(event.startAt) && start)
              ? formatEventTime(start, use24h)
              : '';
            // #3952: mark same-slot suspected duplicates in the "+N more" popover.
            const isDup = suspectedDuplicateIds.has(event.id);
            return (
              <div
                key={occKey}
                className={`xv-day-popover-item${isDup ? ' xv-day-popover-item--dup' : ''}`}
                role="button"
                tabIndex={0}
                title={isDup ? `${event.title} — possible duplicate (same time slot, similar title)` : event.title}
                onClick={() => (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); } }}
              >
                <span className="xv-day-popover-bar" style={{ background: accent }} />
                {event._sourceTaskId ? (
                  <button
                    type="button"
                    className="xv-task-event-check"
                    aria-label={`Complete ${event.title}`}
                    title="Mark complete"
                    onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </button>
                ) : (
                  <span className="xv-day-popover-dot" style={{ background: accent }} />
                )}
                {timeLabel && <span className="xv-day-popover-time">{timeLabel}</span>}
                <span className="xv-day-popover-title">{event.title}</span>
                {isDup && <span className="xv-day-popover-dup">duplicate?</span>}
              </div>
            );
          })}
        </div>
      </div>
    </>,
    document.body,
  );
}

export function MonthView({
  calendarDate,
  expandedEvents,
  suspectedDuplicateIds,
  monthGridRef,
  isToday,
  handleCalendarSlotClick,
  handleEventClick,
  setCalendarDate,
  setTimeFilterState,
  onCompleteTaskEvent,
  onOpenTaskEvent,
}: MonthViewProps) {
  const [hoveredMdEventId, setHoveredMdEventId] = useState<string | null>(null);
  const [use24h] = useCalendar24Hour();
  // Transient red flash on a day requested from a chat card (in-memory; clears on
  // the first mouse move + on a page refresh). See lib/calendarFlash.
  const [flashDate, setFlashDate] = useState<string | null>(() => calendarFlash.get());
  useEffect(() => calendarFlash.subscribe(setFlashDate), []);
  useEffect(() => {
    if (!flashDate) return;
    const clear = () => { setFlashDate(null); calendarFlash.clear(); };
    window.addEventListener('mousemove', clear, { once: true });
    return () => window.removeEventListener('mousemove', clear);
  }, [flashDate]);

  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const days = useMemo(() => getCalendarDays(year, month), [year, month]);
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const numWeeks = Math.ceil(days.length / 7);
  const MAX_VISIBLE = 3;

  // Google-style "+N more" day popover: holds the clicked day + its cell rect.
  const [morePopover, setMorePopover] = useState<{ date: Date; rect: DOMRect } | null>(null);

  const allVisibleEvents = useMemo(
    () => expandedEvents.filter((e: any) => !e.archived && !e.cancelledOccurrence),
    [expandedEvents],
  );

  // Multi-day events get stable, reserved lanes across each week so their bars
  // render unbroken on the top rows. Single-day events fill the rows beneath.
  // Memoized so hover state (hoveredMdEventId) re-renders don't recompute the
  // whole month layout.
  const { byDay: mdByDay, laneCountByWeek } = useMemo(
    () => layoutMultiDaySpans(days, allVisibleEvents),
    [days, allVisibleEvents],
  );

  // Single-day events that START on this date (multi-day spans are reserved
  // separately via mdByDay). All-day first, then timed by start time.
  const getSingleDayEvents = (date: Date) => {
    const dayOnly = startOfLocalDay(date);
    return allVisibleEvents.filter(e => {
      if (isMultiDayEvent(e)) return false;
      const eventStart = parseEventDate(e.startAt);
      if (!eventStart) return false;
      return startOfLocalDay(eventStart).getTime() === dayOnly.getTime();
    }).sort((a, b) => {
      const aAllDay = isAllDayish(a);
      const bAllDay = isAllDayish(b);
      if (aAllDay && !bAllDay) return -1;
      if (!aAllDay && bAllDay) return 1;
      return (parseEventDate(a.startAt)?.getTime() ?? 0) - (parseEventDate(b.startAt)?.getTime() ?? 0);
    });
  };

  // Every event/task on a day (multi-day spans covering it + single-day events),
  // ordered all-day first then by start time. Used by the "+N more" popover.
  const getDayEvents = (date: Date) => {
    const dayOnly = startOfLocalDay(date).getTime();
    return allVisibleEvents.filter(e => {
      const start = parseEventDate(e.startAt);
      if (!start) return false;
      if (isMultiDayEvent(e)) {
        const s = startOfLocalDay(start).getTime();
        const end = (getLastOccupiedDay(e) ?? startOfLocalDay(parseEventDate(e.endAt!) ?? start)).getTime();
        return dayOnly >= s && dayOnly <= end;
      }
      return startOfLocalDay(start).getTime() === dayOnly;
    }).sort((a, b) => {
      const aAllDay = isAllDayish(a);
      const bAllDay = isAllDayish(b);
      if (aAllDay && !bAllDay) return -1;
      if (!aAllDay && bAllDay) return 1;
      return (parseEventDate(a.startAt)?.getTime() ?? 0) - (parseEventDate(b.startAt)?.getTime() ?? 0);
    });
  };

  // Multi-day position: start / mid / end + whether to show title
  const getMdInfo = (event: EventObject, date: Date, dayIdx: number) => {
    if (!isMultiDayEvent(event)) return { mdClass: '', showTitle: true };
    const eventStart = startOfLocalDay(parseEventDate(event.startAt)!);
    // Last occupied day (all-day end is EXCLUSIVE — decrement by one).
    const eventEnd = getLastOccupiedDay(event) ?? startOfLocalDay(parseEventDate(event.endAt!)!);
    const dayOnly = startOfLocalDay(date);
    const isStart = dayOnly.getTime() === eventStart.getTime();
    const isEnd = dayOnly.getTime() === eventEnd.getTime();
    const isSunday = dayIdx % 7 === 0;
    const mdClass = isStart ? 'gcal-md-start' : isEnd ? 'gcal-md-end' : 'gcal-md-mid';
    // Show title on first day of event, or on Sunday (week continuation)
    const showTitle = isStart || isSunday;
    return { mdClass, showTitle };
  };

  return (
    <div ref={monthGridRef} className="gcal-month">
      <div className="gcal-header">
        {dayNames.map((d, i) => (
          <div key={i} className="gcal-header-cell">{d}</div>
        ))}
      </div>

      <div
        className="gcal-grid"
        style={{ gridTemplateRows: `repeat(${numWeeks}, 1fr)`, flex: 1, minHeight: `${numWeeks * 84}px` }}
      >
        {days.map((day, idx) => {
          const weekIdx = Math.floor(idx / 7);
          const laneCount = laneCountByWeek[weekIdx] ?? 0;
          const laneMap = mdByDay.get(idx);
          const singleEvents = getSingleDayEvents(day.date);
          const isTodayDate = isToday(day.date);
          const isFlash = flashDate === `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, '0')}-${String(day.date.getDate()).padStart(2, '0')}`;
          // Single-day events get their OWN budget, independent of the reserved
          // multi-day band — so a quiet day doesn't lose slots (or hide all its
          // events) just because other days in the week are busy with trips.
          // Multi-day bars are never capped (the whole point of the band); a very
          // dense multi-day week simply renders a taller cell rather than hiding
          // a spanning event mid-bar.
          const visibleSingle = singleEvents.slice(0, MAX_VISIBLE);
          const hiddenCount = singleEvents.length - visibleSingle.length;

          return (
            <DroppableDay
              key={idx}
              date={day.date}
              className={`gcal-day ${!day.isCurrentMonth ? 'other-month' : ''}`}
              onClick={() => handleCalendarSlotClick(day.date, 9)}
            >
              <div style={{ height: '100%' }}>
              <div
                className={`gcal-day-number ${isTodayDate ? 'today' : ''}${isFlash ? ' flash-day' : ''}`}
                onClick={(e) => { e.stopPropagation(); setCalendarDate(new Date(day.date)); setTimeFilterState('today'); }}
              >
                {day.date.getDate()}
              </div>
              {/* Reserved multi-day lanes: identical lane index across the whole
                  week keeps each spanning bar on one continuous line. Empty lanes
                  render a hidden spacer so the single-day band below stays aligned
                  across the seven day cells. */}
              {Array.from({ length: laneCount }, (_, lane) => {
                const event = laneMap?.get(lane);
                if (!event) {
                  return (
                    <div key={`md-spacer-${lane}`} className="gcal-event" aria-hidden style={{ visibility: 'hidden' }}>
                      &nbsp;
                    </div>
                  );
                }
                const { mdClass, showTitle } = getMdInfo(event, day.date, idx);
                const bg = getEventBg(event.eventType, true, event.metadata?.customColor);
                const occKey = event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id;
                // #3952: same-slot suspected-duplicate marker on the month chip.
                const mdDupClass = suspectedDuplicateIds.has(event.id) ? ' gcal-event--dup' : '';
                // #3951: outbound-push failure marker on the month chip.
                const mdSyncErrorClass = event.providerSyncState === 'error' ? ' gcal-event--sync-error' : '';
                const mdSyncErrorTitle = event.providerSyncState === 'error' ? ' — not synced to Google (push failed; open to retry)' : '';
                return (
                  <DraggableEvent
                    key={occKey}
                    event={event}
                    onClick={(e) => { e.stopPropagation(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); }}
                  >
                    <div
                      className={`gcal-event gcal-event-allday ${mdClass}${mdDupClass}${mdSyncErrorClass}`}
                      style={{ position: 'relative', overflow: 'hidden', ...(bg ? { background: bg, ...(hoveredMdEventId === occKey ? { opacity: 0.7 } : {}) } : {}) }}
                      title={`${suspectedDuplicateIds.has(event.id) ? `${event.title} — possible duplicate` : event.title}${mdSyncErrorTitle}`}
                      onMouseEnter={() => setHoveredMdEventId(occKey)}
                      onMouseLeave={() => setHoveredMdEventId(null)}
                    >
                      {event._sourceTaskId && (
                        <button
                          type="button"
                          className="xv-task-event-check"
                          aria-label={`Complete ${event.title}`}
                          title="Mark complete"
                          onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        </button>
                      )}
                      <AgentShimmer objectId={event.id} />
                      <span className="gcal-event-title" style={!showTitle ? { visibility: 'hidden' } : undefined}>{event.title}</span>
                    </div>
                  </DraggableEvent>
                );
              })}
              {/* Single-day events render beneath the reserved multi-day band. */}
              {visibleSingle.map((event) => {
                const time = formatTimeCompact(event.startAt, event.allDay, use24h);
                const isAllDay = event.allDay || isDateOnlyString(event.startAt);
                const isTimedTask = !!event._sourceTaskId && !isAllDay;
                // Multi-word names may wrap onto line(s) below; a single word never
                // drops down — it truncates with an ellipsis instead.
                const isMultiWord = /\s/.test((event.title || '').trim());
                const dotColor = getEventDotColor(event.eventType, event.providerCalendarId, event.metadata?.customColor);
                // All-day events fill; a timed TASK gets the same tinted fill so it
                // reads as purple in the month grid (regular timed events stay plain).
                const bg = isAllDay
                  ? getEventBg(event.eventType, true, event.metadata?.customColor)
                  : (isTimedTask ? getEventBg(event.eventType, false, event.metadata?.customColor) : undefined);
                const occKey = event._isVirtual ? `${event.id}-${event._originalStartAt}` : event.id;
                const checkButton = event._sourceTaskId ? (
                  <button
                    type="button"
                    className="xv-task-event-check"
                    aria-label={`Complete ${event.title}`}
                    title="Mark complete"
                    onClick={(e) => { e.stopPropagation(); onCompleteTaskEvent(event); }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                  </button>
                ) : null;
                const taskModifier = isTimedTask
                  ? ` gcal-event-task ${isMultiWord ? 'gcal-event-task--multiword' : 'gcal-event-task--oneword'}`
                  : '';
                // #3952: same-slot suspected-duplicate marker on the month chip.
                const sdDupClass = suspectedDuplicateIds.has(event.id) ? ' gcal-event--dup' : '';
                // #3951: outbound-push failure marker on the month chip.
                const sdSyncErrorClass = event.providerSyncState === 'error' ? ' gcal-event--sync-error' : '';
                const sdSyncErrorTitle = event.providerSyncState === 'error' ? ' — not synced to Google (push failed; open to retry)' : '';
                const sdBaseTitle = time ? `${time} ${event.title}` : event.title;
                return (
                  <DraggableEvent
                    key={occKey}
                    event={event}
                    onClick={(e) => { e.stopPropagation(); (event._sourceTaskId ? onOpenTaskEvent : handleEventClick)(event); }}
                  >
                    <div
                      className={`gcal-event ${isAllDay ? 'gcal-event-allday' : 'gcal-event-timed'}${taskModifier}${sdDupClass}${sdSyncErrorClass}`}
                      style={{ position: 'relative', overflow: 'hidden', ...(bg ? { background: bg } : {}) }}
                      title={`${sdDupClass ? `${sdBaseTitle} — possible duplicate` : sdBaseTitle}${sdSyncErrorTitle}`}
                    >
                      <AgentShimmer objectId={event.id} />
                      {isTimedTask && isMultiWord ? (
                        <>
                          {/* Multi-word: check + time lead, then the name flows after
                              and wraps onto the line(s) below only when it overflows. */}
                          <span className="gcal-task-lead">
                            {checkButton}
                            {time && <span className="gcal-event-time">{time}</span>}
                          </span>
                          <span className="gcal-event-title">{event.title}</span>
                        </>
                      ) : (
                        <>
                          {/* One row: timed single-word task (name truncates with an
                              ellipsis), all-day task (check inline), or a regular event. */}
                          {checkButton}
                          {!isAllDay && !event._sourceTaskId && <span className="gcal-event-dot" style={{ background: dotColor }} />}
                          {time && <span className="gcal-event-time">{time}</span>}
                          <span className="gcal-event-title">{event.title}</span>
                        </>
                      )}
                    </div>
                  </DraggableEvent>
                );
              })}
              {hiddenCount > 0 && (
                <div
                  className="gcal-more"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    const cell = (e.currentTarget.closest('.gcal-day') as HTMLElement) ?? e.currentTarget;
                    setMorePopover({ date: day.date, rect: cell.getBoundingClientRect() });
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const cell = (e.currentTarget.closest('.gcal-day') as HTMLElement) ?? e.currentTarget;
                      setMorePopover({ date: day.date, rect: cell.getBoundingClientRect() });
                    }
                  }}
                >
                  +{hiddenCount} more
                </div>
              )}
              </div>
            </DroppableDay>
          );
        })}
      </div>
      {morePopover && (
        <DayEventsPopover
          date={morePopover.date}
          rect={morePopover.rect}
          events={getDayEvents(morePopover.date)}
          suspectedDuplicateIds={suspectedDuplicateIds}
          onClose={() => setMorePopover(null)}
          onCompleteTaskEvent={(ev) => { setMorePopover(null); onCompleteTaskEvent(ev); }}
          onOpenTaskEvent={(ev) => { setMorePopover(null); onOpenTaskEvent(ev); }}
          handleEventClick={(ev) => { setMorePopover(null); handleEventClick(ev); }}
        />
      )}
    </div>
  );
}
